import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Renders a preview of a Google-style advertisement using a headline, company name, and an optional background image.
 * @component
 * @selector app-google-ad-preview
 * @example
 *   <app-google-ad-preview
 *     [adTitle]="'Spring Sale - 50% Off!'"
 *     [companyName]="'Acme Corp'"
 *     [backgroundImage]="'https://example.com/image.jpg'">
 *   </app-google-ad-preview>
 * @input {string} adTitle - Primary headline text shown in the ad. Defaults to '50% Off Car Wash!'.
 * @input {string} companyName - Company or brand name displayed with the ad. Defaults to 'Company name'.
 * @input {string|undefined} backgroundImage - Optional URL for the background image. When provided and non-empty, the component uses it as the ad background.
 * @output none - This component does not emit any output events.
 *
 * Key methods:
 * - get hasImage(): boolean - Returns true when a non-empty backgroundImage is present (truthy and not just whitespace). Used to conditionally render image-dependent markup.
 *
 * Lifecycle hooks:
 * - This component does not implement any Angular lifecycle hooks (ngOnInit, ngOnChanges, etc.). It is a simple presentational component.
 *
 * Notes:
 * - The component is declared as standalone and uses external template and stylesheet files (templateUrl and styleUrls).
 */
@Component({
  selector: 'app-google-ad-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './google-ad-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./google-ad-preview.component.scss'],
})
export class GoogleAdPreviewComponent {
  @Input() adTitle = '50% Off Car Wash!';
  @Input() companyName = 'Company name';
  @Input() backgroundImage?: string;

  get hasImage(): boolean {
    return Boolean(this.backgroundImage && this.backgroundImage.trim() !== '');
  }
}
